/**
 * File: contactMatch.ts
 * Path: packages/config/src/leads/contactMatch.ts
 * Description: How the admin tells whether two records are the same
 * business: phone numbers in one format, website domains and business names
 * with the noise taken out. Used by the lead duplicate warning, and by the
 * client matcher (clients/clientMatching.ts) so leads and clients are
 * compared the same way.
 */

/** Our own number and email never identify a customer. */
const OWN_EMAILS: readonly string[] = [
  "admin@idksolutions.co.za",
  "admin@idksolutions.org",
  "liam@idksolutions.co.za",
];
const OWN_PHONES: readonly string[] = ["+27716249157"];

/**
 * A South African number as "+27XXXXXXXXX" (digits only after the plus), or
 * "" when it isn't a usable number. Accepts 0xx, +27, 27 and 0027 starts and
 * any spaces, dashes, brackets or quote marks Zoho and phones leave in.
 */
export const normalisePhone = (value: string): string => {
  let digits = value.replace(/[^\d+]/g, "");
  if (digits.startsWith("00")) digits = `+${digits.slice(2)}`;
  digits = digits.replace(/\+/g, (_m, offset: number) => (offset === 0 ? "+" : ""));
  if (digits.startsWith("+27")) digits = digits.slice(3);
  else if (digits.startsWith("27") && digits.length === 11) digits = digits.slice(2);
  else if (digits.startsWith("0")) digits = digits.slice(1);
  else if (digits.startsWith("+")) return "";
  return /^\d{9}$/.test(digits) ? `+27${digits}` : "";
};

/**
 * The one way phone numbers are stored: "+27 83 700 1037". A number that
 * can't be read is kept as typed (trimmed), never thrown away.
 */
export const formatPhone = (value: string): string => {
  const normal = normalisePhone(value);
  if (!normal) return value.trim();
  const n = normal.slice(3);
  return `+27 ${n.slice(0, 2)} ${n.slice(2, 5)} ${n.slice(5)}`;
};

export const isOwnPhone = (value: string): boolean => OWN_PHONES.includes(normalisePhone(value));

/** Lower-cased and trimmed, or "" for none. */
export const normaliseEmail = (value: string): string => value.trim().toLowerCase();

export const isOwnEmail = (value: string): boolean => OWN_EMAILS.includes(normaliseEmail(value));

/** "https://www.Joes.co.za/about" → "joes.co.za"; "" when it isn't a site. */
export const websiteDomain = (value: string): string => {
  const raw = value.trim();
  if (!raw) return "";
  try {
    const url = new URL(/^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`);
    const host = url.hostname.toLowerCase().replace(/^www\./, "");
    return host.includes(".") ? host : "";
  } catch {
    return "";
  }
};

/**
 * A business name with the noise taken out, so "The Sir Bastian's Shuttle
 * Service (Pty) Ltd." and "sir bastians shuttle service" compare equal:
 * lower case, "&" as "and", no punctuation, no "(Pty) Ltd" / "CC" / "t/a"
 * trading-as tail, no leading "the".
 */
export const normaliseBusinessName = (value: string): string => {
  let name = value.toLowerCase().replace(/\s+t\/a\s+.*$/, " ");
  name = name.replace(/&/g, " and ");
  name = name.replace(/[’'`]/g, "");
  name = name.replace(/\(pty\)|\bpty\b|\bltd\b|\blimited\b|\bcc\b|\binc\b/g, " ");
  name = name.replace(/[^a-z0-9]+/g, " ").trim();
  return name.replace(/^the\s+/, "").replace(/\s+/g, " ");
};

/** The details two records are compared on. */
export interface MatchDetails {
  name: string;
  phone: string;
  email: string;
  website: string;
}

export type MatchReason = "phone" | "website" | "email" | "name";

export const MATCH_REASON_LABELS: Record<MatchReason, string> = {
  phone: "the same phone number",
  website: "the same website",
  email: "the same email",
  name: "the same name",
};

/** Phones used by two or more records say nothing about who is who. */
const sharedValues = (values: readonly string[]): Set<string> => {
  const seen = new Set<string>();
  const shared = new Set<string>();
  for (const value of values) {
    if (!value) continue;
    if (seen.has(value)) shared.add(value);
    seen.add(value);
  }
  return shared;
};

/**
 * Which of the other records look like `candidate`, and why: same phone,
 * same website domain, same email or same normalised name. Our own phone and
 * email never match, and neither does a phone, email or website held by
 * two or more of the other records (a switchboard says nothing about who is
 * who). `ignoreId` leaves out the record being edited.
 */
export const findLookalikes = <T extends MatchDetails & { id: string }>(
  candidate: MatchDetails,
  others: readonly T[],
  ignoreId?: string,
): Array<{ record: T; reasons: MatchReason[] }> => {
  const pool = others.filter((o) => o.id !== ignoreId);
  const phone = isOwnPhone(candidate.phone) ? "" : normalisePhone(candidate.phone);
  const email = isOwnEmail(candidate.email) ? "" : normaliseEmail(candidate.email);
  const domain = websiteDomain(candidate.website);
  const name = normaliseBusinessName(candidate.name);

  const sharedPhones = sharedValues(pool.map((o) => normalisePhone(o.phone)));
  const sharedEmails = sharedValues(pool.map((o) => normaliseEmail(o.email)));
  const sharedDomains = sharedValues(pool.map((o) => websiteDomain(o.website)));

  const found: Array<{ record: T; reasons: MatchReason[] }> = [];
  for (const record of pool) {
    const reasons: MatchReason[] = [];
    if (phone && normalisePhone(record.phone) === phone && !sharedPhones.has(phone)) reasons.push("phone");
    if (domain && websiteDomain(record.website) === domain && !sharedDomains.has(domain)) reasons.push("website");
    if (email && normaliseEmail(record.email) === email && !sharedEmails.has(email)) reasons.push("email");
    if (name && normaliseBusinessName(record.name) === name) reasons.push("name");
    if (reasons.length > 0) found.push({ record, reasons });
  }
  return found;
};
